import { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Tooltip } from '@mantine/core';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import type { SessionDrillDown, SessionDrillDownCard, CoachInsightDetail } from '../../../../types';
import { DataLibraryDrawer } from '../../../../components/DataLibrary/DataLibraryDrawer';
import type { ActiveFilterRow } from '../../../../components/common/FilterPanel/FilterPanel';
import { getAirtimeColorTier } from '../../../../config/overviewConfig';
import classes from './SessionHealthDrillDown.module.css';

type TabKey = 'completed' | 'upcoming' | 'flagged';

interface SessionHealthDrillDownProps {
  drillDown: SessionDrillDown;
  cohortId?: string;
  onAddTask?: (session: SessionDrillDownCard) => void;
  onTabChange?: () => void;
  /**
   * MED-1205 (AC6): show the leader-airtime metric. Gated upstream to
   * group-coaching cohorts with live_session_delivery on (flag AND modality).
   */
  showAirtime?: boolean;
  /**
   * MED-1205 (AC2): the cohort's leadership modality, selecting the coach-led
   * vs peer-led airtime color thresholds. Only meaningful when showAirtime.
   */
  isPeerLed?: boolean;
}

/** MED-1205: airtime tier → CSS module color class. */
const AIRTIME_TIER_CLASS: Record<ReturnType<typeof getAirtimeColorTier>, string> = {
  red: classes.metricValueAirtimeRed,
  orange: classes.metricValueAirtimeOrange,
  green: classes.metricValueAirtimeGreen,
};

const TAB_CONFIG: { key: TabKey; label: string }[] = [
  { key: 'completed', label: 'Completed' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'flagged', label: 'Flagged' },
];

const ZERO_STATES: Record<TabKey, string> = {
  completed: 'No completed sessions yet',
  upcoming: 'No upcoming sessions',
  flagged: 'No flagged sessions',
};

const STATUS_CLASS: Record<SessionDrillDownCard['status'], string> = {
  complete: classes.statusComplete,
  partial: classes.statusPartial,
  flagged: classes.statusFlagged,
  scheduled: classes.statusScheduled,
};

function CoachInsightModal({
  opened,
  session,
  onClose,
}: {
  opened: boolean;
  session: SessionDrillDownCard | null;
  onClose: () => void;
}) {
  if (!session) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={session.title}
      size="lg"
      centered
    >
      <div className={classes.modalContent}>
        {session.allCoachInsights.map((insight: CoachInsightDetail, idx: number) => (
          <div key={idx} className={classes.modalGroup}>
            <div className={classes.modalAvatar}>
              {insight.coachAvatarUrl ? (
                <img src={insight.coachAvatarUrl} alt={insight.coachName} />
              ) : (
                <span className={classes.modalAvatarInitial}>
                  {insight.coachName.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className={classes.modalGroupInfo}>
              <div className={classes.modalGroupName}>{insight.groupName}</div>
              <div className={classes.modalCoachName}>{insight.coachName}</div>
              <div className={classes.modalInsightText}>&ldquo;{insight.text}&rdquo;</div>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function SessionCard({
  card,
  onSeeMore,
  onAddTask,
  onViewReport,
  showAirtime = false,
  isPeerLed = false,
}: {
  card: SessionDrillDownCard;
  onSeeMore: (card: SessionDrillDownCard) => void;
  onAddTask?: (card: SessionDrillDownCard) => void;
  onViewReport?: (card: SessionDrillDownCard) => void;
  showAirtime?: boolean;
  isPeerLed?: boolean;
}) {
  const isUpcoming = card.status === 'scheduled';
  const isFlagged = card.isFlagged;
  const isComplete = card.status === 'complete';
  const metricColorClass = isUpcoming ? classes.metricValueNA : isFlagged ? classes.metricValueFlagged : isComplete ? classes.metricValueComplete : '';

  // MED-1205: airtime is colored per-threshold (inverted, role-aware), NOT by
  // the shared status class. `null`/upcoming → the gray "not available" state
  // (AC5); a real share picks red/orange/green from the modality thresholds.
  const airtimePct = card.metrics.airtimeLeaderPct;
  const airtimeColorClass =
    !isUpcoming && airtimePct !== null
      ? AIRTIME_TIER_CLASS[getAirtimeColorTier(airtimePct, isPeerLed)]
      : classes.metricValueNA;

  return (
    <div
      className={`${classes.sessionCard} ${isFlagged ? classes.sessionCardFlagged : ''}`}
    >
      {/* Header */}
      <div className={classes.cardHeader}>
        <div className={classes.cardHeaderLeft}>
          <div className={classes.cardTitle}>{card.title}</div>
          <div className={classes.cardDate}>{card.date}</div>
        </div>
        {isFlagged && card.flagReasons.length > 0 ? (
          <Tooltip
            /* MED-1288 (AC6): was `card.flagReasons.join('\n')`. A "\n" inside
               an HTML text node collapses to a space, so multiple reasons ran
               together into one wrapped blob — and since two groups at the same
               percentage produced identical text, it read as one warning
               repeated. Discrete elements per reason; a single reason renders as
               plain text with no list marker (AC7). */
            label={
              card.flagReasons.length === 1 ? (
                card.flagReasons[0]
              ) : (
                <span className={classes.flagReasonList}>
                  {card.flagReasons.map((reason, i) => (
                    <span key={i} className={classes.flagReason}>
                      {reason}
                    </span>
                  ))}
                </span>
              )
            }
            multiline
            w={260}
            withArrow
            position="bottom"
          >
            <span className={`${classes.statusPill} ${STATUS_CLASS[card.status]}`}>
              {card.statusLabel}
            </span>
          </Tooltip>
        ) : (
          <span className={`${classes.statusPill} ${STATUS_CLASS[card.status]}`}>
            {card.statusLabel}
          </span>
        )}
      </div>

      {/* Metrics */}
      <div className={`${classes.metricsRow} ${showAirtime ? classes.metricsRowWithAirtime : ''}`}>
        <div className={classes.metric}>
          <div
            className={`${classes.metricValue} ${metricColorClass}`}
          >
            {isUpcoming ? 'N/A' : card.metrics.attendPercent !== null ? `${card.metrics.attendPercent}%` : 'N/A'}
          </div>
          <div className={classes.metricLabel}>Avg Attendance</div>
        </div>
        <div className={classes.metric}>
          <div
            className={`${classes.metricValue} ${metricColorClass}`}
          >
            {isUpcoming ? 'N/A' : card.metrics.engageAvg !== null ? card.metrics.engageAvg.toFixed(1) : 'N/A'}
          </div>
          <div className={classes.metricLabel}>Avg Engagement</div>
        </div>
        <div className={classes.metric}>
          <div
            className={`${classes.metricValue} ${metricColorClass}`}
          >
            {isUpcoming ? 'N/A' : card.metrics.rsvpPercent !== null ? `${card.metrics.rsvpPercent}%` : 'N/A'}
          </div>
          <div className={classes.metricLabel}>RSVP</div>
        </div>
        {/* MED-1205 (AC1): facilitator airtime — 4th metric, group-coaching + LSD only.
            AC4: single aggregate leader share, no participant names. */}
        {showAirtime && (
          <div className={classes.metric}>
            <div
              className={`${classes.metricValue} ${airtimeColorClass}`}
            >
              {isUpcoming ? 'N/A' : airtimePct !== null ? `${airtimePct}%` : 'N/A'}
            </div>
            <div className={classes.metricLabel}>Leader Airtime</div>
          </div>
        )}
      </div>

      {/* Coach Insight */}
      {!isUpcoming && card.coachInsight && (
        <div className={`${classes.coachInsight} ${isFlagged ? classes.coachInsightFlagged : ''}`}>
          <div className={`${classes.coachInsightLabel} ${isFlagged ? classes.coachInsightLabelFlagged : ''}`}>
            Coach Insight
          </div>
          <div className={classes.coachInsightText}>
            &ldquo;{card.coachInsight.text}&rdquo;
            <button
              className={classes.seeMoreLink}
              onClick={(e) => {
                e.stopPropagation();
                onSeeMore(card);
              }}
            >
              see more
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className={classes.cardActions}>
        <button className={classes.viewReportLink} onClick={() => onViewReport?.(card)}>
          View Full Report <ArrowRight size={14} />
        </button>
        {isFlagged && onAddTask && (
          <button
            className={classes.addTaskButton}
            onClick={() => onAddTask(card)}
          >
            Add Task
          </button>
        )}
      </div>
    </div>
  );
}

export function SessionHealthDrillDown({ drillDown, cohortId, onAddTask, onTabChange, showAirtime = false, isPeerLed = false }: SessionHealthDrillDownProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('completed');
  const [modalSession, setModalSession] = useState<SessionDrillDownCard | null>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [drawerLayoutKey, setDrawerLayoutKey] = useState(0);
  const [drawerFilters, setDrawerFilters] = useState<ActiveFilterRow[] | undefined>(undefined);

  const sessions = drillDown.tabs[activeTab].sessions;

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 10);
    setShowRightArrow(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  // Scroll to the right on mount and tab change (show most recent sessions)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Wait for layout
    requestAnimationFrame(() => {
      el.scrollLeft = el.scrollWidth;
      updateArrows();
    });
  }, [activeTab, updateArrows]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.clientWidth / 3;
    el.scrollBy({ left: direction === 'left' ? -cardWidth : cardWidth, behavior: 'smooth' });
  };

  const handleSeeMore = (card: SessionDrillDownCard) => {
    setModalSession(card);
  };

  const handleViewReport = useCallback((card: SessionDrillDownCard) => {
    setDrawerFilters([{ key: 'session_number', values: [String(card.sessionNumber)] }]);
    setDrawerLayoutKey(k => k + 1);
    setDrawerOpened(true);
  }, []);

  return (
    <>
    <DataLibraryDrawer
      opened={drawerOpened}
      onClose={() => setDrawerOpened(false)}
      defaultCohortId={cohortId}
      defaultTab="sessions"
      defaultFilters={drawerFilters}
      layoutKey={drawerLayoutKey}
    />
    <div className={classes.drillDown}>
      {/* Section Header */}
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <span className={classes.headerTitle}>Session Health</span>
          {drillDown.flaggedCount > 0 && (
            <span className={classes.flaggedBadge}>
              {drillDown.flaggedCount} session{drillDown.flaggedCount !== 1 ? 's' : ''} flagged
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className={classes.tabs}>
        {TAB_CONFIG.map(({ key, label }) => {
          const count = drillDown.tabs[key].count;
          const isActive = activeTab === key;
          const isFlaggedTab = key === 'flagged';
          return (
            <button
              key={key}
              className={`${classes.tab} ${isActive ? classes.tabActive : ''}`}
              onClick={() => { setActiveTab(key); onTabChange?.(); }}
            >
              {label}
              <span
                className={`${classes.tabCount} ${isFlaggedTab && count > 0 ? classes.tabFlaggedCount : ''}`}
              >
                {count}
              </span>
            </button>
          );
        })}
        <button
          className={classes.viewAllLink}
          style={{ marginLeft: 'auto' }}
          onClick={() => {
            setDrawerFilters([{ key: 'session_status', values: [activeTab] }]);
            setDrawerLayoutKey(k => k + 1);
            setDrawerOpened(true);
          }}
        >
          View in Data Library <ChevronRight size={14} />
        </button>
      </div>

      {/* Session Cards */}
      {sessions.length === 0 ? (
        <div className={classes.emptyState}>{ZERO_STATES[activeTab]}</div>
      ) : (
        <div className={classes.scrollContainer}>
          {showLeftArrow && (
            <button
              className={`${classes.scrollArrow} ${classes.scrollArrowLeft}`}
              onClick={() => scroll('left')}
              aria-label="Scroll left"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          <div className={classes.scrollTrack} ref={scrollRef}>
            {sessions.map(card => (
              <SessionCard
                key={card.sessionNumber}
                card={card}
                onSeeMore={handleSeeMore}
                onAddTask={onAddTask}
                onViewReport={handleViewReport}
                showAirtime={showAirtime}
                isPeerLed={isPeerLed}
              />
            ))}
          </div>
          {showRightArrow && (
            <button
              className={`${classes.scrollArrow} ${classes.scrollArrowRight}`}
              onClick={() => scroll('right')}
              aria-label="Scroll right"
            >
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      )}

      {/* Coach Insights Modal */}
      <CoachInsightModal
        opened={!!modalSession}
        session={modalSession}
        onClose={() => setModalSession(null)}
      />
    </div>
    </>
  );
}
